import {
  Alert,
  Button,
  Code,
  Collapse,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Warning } from "@phosphor-icons/react";
import {
  isRouteErrorResponse,
  useRouteError,
  useNavigate,
} from "react-router-dom";

export default function DashboardErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const [detailsOpen, { toggle }] = useDisclosure(false);

  const isAxios = (error as any)?.isAxiosError;
  const isTimeout =
    isAxios &&
    ((error as any)?.code === "ECONNABORTED" ||
      (error as any)?.message?.includes("timeout"));

  const title = isTimeout
    ? "Request timed out"
    : isRouteErrorResponse(error)
      ? `${error.status} — ${error.statusText}`
      : "Something went wrong";

  const description = isTimeout
    ? "A request to the server took too long to respond. This is usually temporary — try reloading the page."
    : isRouteErrorResponse(error)
      ? error.data
      : ((error as any)?.message ?? "An unexpected error occurred.");

  const details: Record<string, unknown> = {};
  if (isAxios) {
    details["Request URL"] =
      `${(error as any).config?.baseURL ?? ""}${(error as any).config?.url ?? ""}`;
    details["Method"] = (error as any).config?.method?.toUpperCase();
    details["Error code"] = (error as any).code;
    details["HTTP status"] = (error as any).response?.status ?? "no response";
    details["Response data"] = (error as any).response?.data ?? null;
  } else if (error instanceof Error) {
    details["Error name"] = error.name;
    details["Message"] = error.message;
    details["Stack"] = error.stack;
  } else if (isRouteErrorResponse(error)) {
    details["Status"] = error.status;
    details["Data"] = error.data;
  }

  details["Page"] = window.location.href;
  details["Time"] = new Date().toISOString();

  return (
    <div className="grid max-w-2xl gap-6 px-x py-y">
      <Alert
        icon={<Warning size={20} />}
        title={title}
        color="red"
        variant="light"
      >
        {description}
      </Alert>

      <Stack gap="sm">
        <div className="flex gap-3">
          <Button variant="filled" size="sm" onClick={() => navigate(0)}>
            Reload page
          </Button>
          <Button
            variant="subtle"
            size="sm"
            color="gray"
            onClick={() => navigate(-1)}
          >
            Go back
          </Button>
          <Button
            variant="subtle"
            size="sm"
            color="gray"
            ml="auto"
            onClick={toggle}
          >
            {detailsOpen ? "Hide details" : "Show details"}
          </Button>
        </div>

        <Collapse in={detailsOpen}>
          <Stack gap="xs">
            {Object.entries(details).map(([key, value]) =>
              value != null ? (
                <div key={key}>
                  <Text size="xs" c="dimmed" mb={2}>
                    {key}
                  </Text>
                  <Code
                    block
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                  >
                    {typeof value === "string"
                      ? value
                      : JSON.stringify(value, null, 2)}
                  </Code>
                </div>
              ) : null
            )}
          </Stack>
        </Collapse>
      </Stack>
    </div>
  );
}
